/**
 * useSprintDetailFeatures Hook
 * 
 * Composes feature hooks for SprintDetail.
 * Groups: setup, toolbar, navigation, modals, nested sprints, fokus mode, campaign views.
 * 
 * UNIFIED VIEW MODEL:
 * All views are stored in sprint.views array. The activeCustomView from useCampaignViews
 * is always the currently selected view (there's no separate "default" view concept).
 * useCustomViewSettings handles saving/restoring settings when switching between views.
 */
import { useCallback, useRef } from 'react';
import { useTabs } from '../../../context/TabsContext';
import useSprintDetailSetup from './useSprintDetailSetup';
import { useToolbarState } from './useToolbarState';
import useInitialNavigation from './useInitialNavigation';
import { useModals } from './useModals';
import { useNestedSprints } from './useNestedSprints';
import useFokusModeRefs, { useFokusRefsConnection } from './useFokusModeRefs';
import useCampaignViews from './useCampaignViews';
import useFlowViewVisibility from './useFlowViewVisibility';
import { useCustomViewSettings } from './customViewSettings';
import useWrappedToolbar from './useWrappedToolbar';
import useClearLockedDecisionAnswers from './useClearLockedDecisionAnswers';
import { recallCampaignView, rememberCampaignView } from './campaignViewMemory';

const useSprintDetailFeatures = ({
  // From core
  localSprint,
  setLocalSprint,
  actions,
  state,
  // Props
  allSprints,
  onSprintIconChange,
  onSelectSprint,
  onAddSprint,
  onUpdateSprint,
  initialView,
  initialMissionFocus,
  targetCampaignId = null,
  onViewChanged,
  onMissionFocused,
  onViewModeChange,
  onCustomViewChange,
  settings,
  isMultiCampaignMode = false,
  activeView = null,
}) => {
  const { activeTab } = useTabs();
  const viewMemoryRef = useRef({ tabId: null, campaignId: null });
  viewMemoryRef.current = {
    tabId: activeTab?.type === 'campaign' || activeTab?.type === 'template' ? activeTab.id : null,
    campaignId: localSprint?.id || null,
  };
  const {
    flowchartRef,
    setFiltersPerView,
    setShowArchived,
    setShowActionableOnly,
    setShowNestedCampaigns,
    setKanbanSelectedIds,
    setTableSelectedIds,
    kanbanSelectedIds,
    tableSelectedIds,
    ganttSelectedIds,
    todoSelectedIds,
    neuralSelectedIds,
    tableColumnVisibility,
    setTableColumnVisibility,
    setTodoElementVisibility,
    setTodoViewMode,
    setTodoSwimlaneGroupBy,
    localFlowViewVisibility,
    setLocalFlowViewVisibility,
    DEFAULT_FLOW_VISIBILITY,
  } = state;

  // Setup: derived data and settings handlers
  const setup = useSprintDetailSetup({
    localSprint, setLocalSprint, actions, onSprintIconChange,
    setFiltersPerView, setShowArchived, setShowActionableOnly,
    setKanbanSelectedIds, setTableSelectedIds,
    settings,
  });

  // Toolbar state - pass initialView to avoid flash of default view
  // Extract the view type from initialView (could be string or object with type)
  const initialViewType = typeof initialView === 'object' ? initialView?.type : initialView;
  const toolbar = useToolbarState(flowchartRef, localSprint?.id, initialViewType);

  // Modals
  const modals = useModals(setup.tasks, actions.handleTaskStatusChange);

  // Nested sprints
  const nested = useNestedSprints({
    localSprint, setLocalSprint, allSprints,
    onSelectSprint, onAddSprint, onUpdateSprint,
    onCreateNestedSprint: actions.handleCreateNestedSprint,
  });

  useClearLockedDecisionAnswers({
    localSprint,
    setLocalSprint,
    nestedSprints: nested.nestedSprints,
  });

  // FOKUS mode refs for all views
  const fokusModeRefs = useFokusModeRefs({
    tasks: setup.tasks, statuses: setup.statuses, nestedSprints: nested.nestedSprints,
    kanbanSelectedIds, tableSelectedIds, ganttSelectedIds, todoSelectedIds, neuralSelectedIds,
  });

  // Campaign views (all views are now stored here - no more "default" vs "custom")
  const multiViewMode = activeView?.viewMode || 'flow';
  // DON'T pass setCurrentView here - it causes race conditions with URL navigation
  // The view type is now set by useInitialNavigation based on the URL/preferredDetailView
  const campaignViews = useCampaignViews(
    localSprint?.id, 
    localSprint, 
    setLocalSprint, 
    isMultiCampaignMode, 
    multiViewMode,
    null // Don't auto-sync view type - let useInitialNavigation handle it
  );

  // Wrap selectView to also update URL with custom view ID, set toolbar view type, and add to history
  // @param {boolean} skipUrlUpdate - If true, don't update the URL (used when restoring from history)
  const selectViewWithUrlSync = useCallback((viewId, skipUrlUpdate = false) => {
    // Find the view first to validate it exists
    const view = campaignViews.customViews.find(v => v.id === viewId);
    console.log('[selectViewWithUrlSync] viewId:', viewId, 'found:', !!view, 'type:', view?.type);
    
    if (view) {
      // View exists - select it, set toolbar view type, update URL, and add to history
      rememberCampaignView(viewMemoryRef.current.tabId, viewMemoryRef.current.campaignId, view);
      campaignViews.selectView(viewId);
      // Set the toolbar's current view to match the custom view's type
      console.log('[selectViewWithUrlSync] Calling toolbar.setCurrentView with:', view.type);
      toolbar.setCurrentView(view.type);
      if (!skipUrlUpdate) {
        // Update URL
        if (onCustomViewChange) {
          onCustomViewChange(viewId, view.type);
        }
        // Add to tab history (for back/forward navigation)
        if (onViewModeChange) {
          onViewModeChange(view.type, viewId);
        }
      }
      return view;
    }

    // View doesn't exist yet (still loading, or a stale id). Restoring a tab
    // must not jump to Flow — the caller selects by view type once the list is ready.
    if (skipUrlUpdate) return null;

    console.warn(`[useSprintDetailFeatures] View ${viewId} not found, falling back to first view`);
    const fallbackView = campaignViews.customViews[0];
    if (!fallbackView) return null;
    campaignViews.selectView(fallbackView.id);
    toolbar.setCurrentView(fallbackView.type);
    if (!skipUrlUpdate) {
      onCustomViewChange?.(fallbackView.id, fallbackView.type);
      onViewModeChange?.(fallbackView.type, fallbackView.id);
    }
    return skipUrlUpdate ? null : fallbackView;
  }, [campaignViews.selectView, campaignViews.customViews, toolbar.setCurrentView, onCustomViewChange, onViewModeChange]);

  // Wrap selectViewByType to also update URL with the selected view's ID and add to history
  // @param {boolean} skipUrlUpdate - If true, don't update the URL (used when restoring from history)
  const selectViewByTypeWithUrlSync = useCallback((viewType, skipUrlUpdate = false) => {
    const selectedView = campaignViews.selectViewByType(viewType);
    if (selectedView) {
      rememberCampaignView(viewMemoryRef.current.tabId, viewMemoryRef.current.campaignId, selectedView);
      // Set the toolbar's current view
      toolbar.setCurrentView(selectedView.type);
      if (!skipUrlUpdate) {
        // Update URL
        if (onCustomViewChange) {
          onCustomViewChange(selectedView.id, selectedView.type);
        }
        // Add to tab history (for back/forward navigation)
        if (onViewModeChange) {
          onViewModeChange(selectedView.type, selectedView.id);
        }
      }
    }
    return selectedView;
  }, [campaignViews.selectViewByType, toolbar.setCurrentView, onCustomViewChange, onViewModeChange]);

  // Initial navigation (restore view/focus from props)
  // Must be after campaignViews so we can select custom views
  // Uses raw toolbar.setCurrentView - history is managed by selectViewWithUrlSync/selectViewByTypeWithUrlSync
  // 
  // Check if initialView includes skip flags - if so, skip URL updates
  // - isHistoryNavigation: navigating via browser back/forward
  // - skipUrlUpdate: navigating via URL change (OpenInViewDropdown, direct URL)
  const shouldSkipUrlUpdate = typeof initialView === 'object' && 
    (initialView?.isHistoryNavigation || initialView?.skipUrlUpdate);
  useInitialNavigation({
    initialView, initialMissionFocus, tasks: setup.tasks,
    currentView: toolbar.currentView, setCurrentView: toolbar.setCurrentView,
    onViewChanged, onMissionFocused,
    onSelectCustomView: selectViewWithUrlSync,
    onSelectViewByType: selectViewByTypeWithUrlSync, // Use URL-synced version
    viewsReady: campaignViews.viewsReady,
    views: campaignViews.customViews,
    rememberedView: recallCampaignView(viewMemoryRef.current.tabId, viewMemoryRef.current.campaignId),
    skipUrlUpdate: shouldSkipUrlUpdate, // Skip URL updates when navigating via URL or history
    campaignId: localSprint?.id, // Used to reset refs when campaign changes
    targetCampaignId,
  });

  // Flow view visibility - now uses activeCustomView settings directly
  const { effectiveFlowViewVisibility, handleFlowViewVisibilityChangeInternal } = useFlowViewVisibility({
    localFlowViewVisibility,
    setLocalFlowViewVisibility,
    defaultFlowVisibility: DEFAULT_FLOW_VISIBILITY,
    activeCustomView: campaignViews.activeCustomView,
    updateView: campaignViews.updateView,
  });

  // Custom view settings synchronization
  // Note: With unified view model, activeCustomView is always set (it's the current view)
  const customViewSettings = useCustomViewSettings({
    activeCustomView: campaignViews.activeCustomView,
    updateView: campaignViews.updateView,
    currentViewType: toolbar.currentView,
    filtersPerView: state.filtersPerView, setFiltersPerView,
    localSprint, setLocalSprint,
    swimlaneSetting: setup.swimlaneSetting,
    tableColumnVisibility, setTableColumnVisibility,
    todoElementVisibility: state.todoElementVisibility, setTodoElementVisibility,
    flowViewVisibility: effectiveFlowViewVisibility,
    onFlowViewVisibilityChange: setLocalFlowViewVisibility,
    ganttSwimlane: toolbar.ganttSwimlane,
    setGanttSwimlane: toolbar.setGanttSwimlaneRaw,
    showArchived: state.showArchived, setShowArchived,
    showActionableOnly: state.showActionableOnly, setShowActionableOnly,
    showNestedCampaigns: state.showNestedCampaigns, setShowNestedCampaigns,
    toolbar, flowchartRef,
    kanbanFokus: fokusModeRefs.kanbanFokus,
    tableFokus: fokusModeRefs.tableFokus,
    ganttFokus: fokusModeRefs.ganttFokus,
    todoFokus: fokusModeRefs.todoFokus,
    neuralFokus: fokusModeRefs.neuralFokus,
    todoViewMode: state.todoViewMode, setTodoViewMode,
    todoSwimlaneGroupBy: state.todoSwimlaneGroupBy, setTodoSwimlaneGroupBy,
    todoSwimlane: state.todoSwimlane, setTodoSwimlane: state.setTodoSwimlane,
    todoShowNestedItems: state.todoShowNestedItems, setTodoShowNestedItems: state.setTodoShowNestedItems,
    flowViewport: null, onFlowViewportChange: null,
    ganttViewport: null, onGanttViewportChange: null,
    onSwitchToDefault: useCallback(() => {
      // With unified view model, switching views is handled by useCustomViewSettings
      setLocalFlowViewVisibility(null);
    }, [setLocalFlowViewVisibility]),
    updateDefaultViewSettings: null, // No longer needed - all views use the same system
  });

  // Connect FOKUS refs to custom view settings
  useFokusRefsConnection(fokusModeRefs, customViewSettings);

  // Wrapped toolbar with custom view FOKUS saving
  // NOTE: Tab history sync is NOT done here anymore - it's handled explicitly
  // in selectViewWithUrlSync and selectViewByTypeWithUrlSync
  const wrappedToolbar = useWrappedToolbar({
    toolbar,
    activeCustomView: campaignViews.activeCustomView,
    customViewSettings,
  });

  // Create wrapped campaignViews with URL-synced selectView
  const campaignViewsWithUrlSync = {
    ...campaignViews,
    selectView: selectViewWithUrlSync,
  };

  return {
    // Setup
    setup,
    
    // Toolbar
    toolbar,
    wrappedToolbar,
    
    // Modals
    modals,
    
    // Nested sprints
    nested,
    
    // FOKUS mode
    fokusModeRefs,
    
    // Campaign views (unified view model - all views stored here)
    // Uses wrapped selectView that syncs to URL
    campaignViews: campaignViewsWithUrlSync,
    
    // Flow visibility
    effectiveFlowViewVisibility,
    handleFlowViewVisibilityChangeInternal,
    
    // View settings synchronization
    customViewSettings,
  };
};

export default useSprintDetailFeatures;
